// src/context/VersionContext.tsx

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { VersionConfig, getVersion } from '../versions/versionRegistry';

interface VersionContextType {
  currentVersion: VersionConfig;
  setCurrentVersion: (versionId: string) => void;
  internalVersion: 'v1' | 'v2' | 'v3';
}

const VersionContext = createContext<VersionContextType | undefined>(undefined);

export const VersionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentVersion, setCurrentVersionState] = useState<VersionConfig | null>(null);

  // ✅ FIXED: Handle null from localStorage properly
  const savedVersion = localStorage.getItem('designSystemVersion');
  const initialVersion = savedVersion ? getVersion(savedVersion) : getVersion('v3.0.0');

  // ✅ Only set state if we have a valid version
  const initialState = initialVersion ? initialVersion : getVersion('v1.1.0')!; // Fallback to v1.1.0 if both fail

  // ✅ Initialize state with guaranteed non-null value
  const [state, setState] = useState<VersionConfig>(() => initialState);

  const setCurrentVersion = (versionId: string) => {
    // ✅ Ensure we have a valid version before setting
    const version = getVersion(versionId);
    if (version) {
      setState(version);
      localStorage.setItem('designSystemVersion', versionId);
    } else {
      // Fallback if version doesn't exist
      console.warn(`Version ${versionId} not found, using default v2.0.0`);
      const defaultVersion = getVersion('v3.0.0');
      if (defaultVersion) {
        setState(defaultVersion);
        localStorage.setItem('designSystemVersion', 'v3.0.0');
      }
    }
  };

  const value: VersionContextType = {
    currentVersion: state,
    setCurrentVersion,
    internalVersion: state.internalId,
  };

  return (
    <VersionContext.Provider value={value}>
      {children}
    </VersionContext.Provider>
  );
};

export const useVersionContext = () => {
  const context = useContext(VersionContext);
  if (!context) {
    throw new Error('useVersionContext must be used within VersionProvider');
  }
  return context;
};
